import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

const REFERRAL_STORAGE_KEY = "betmebro_referral_code";
const REFERRAL_TIMESTAMP_KEY = "betmebro_referral_ts";
const REFERRAL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * useReferralCapture
 *
 * Captures referral codes from URL query parameters (?ref=CODE)
 * and stores them in localStorage for later use during signup/auth.
 *
 * The captured code persists for 30 days.
 */
export function useReferralCapture(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;

    const refCode = searchParams.get("ref");
    if (!refCode || refCode.length < 4) return;

    hasProcessed.current = true;

    // Store the referral code
    try {
      localStorage.setItem(REFERRAL_STORAGE_KEY, refCode.toUpperCase());
      localStorage.setItem(REFERRAL_TIMESTAMP_KEY, Date.now().toString());
    } catch {
      // localStorage might be unavailable in some contexts
      console.warn("Failed to store referral code");
    }

    // Remove ref param from URL to clean it up
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("ref");
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);
}

/**
 * Get the stored referral code, if any and not expired
 */
export function getStoredReferralCode(): string | null {
  try {
    const code = localStorage.getItem(REFERRAL_STORAGE_KEY);
    const timestamp = localStorage.getItem(REFERRAL_TIMESTAMP_KEY);

    if (!code || !timestamp) return null;

    const age = Date.now() - parseInt(timestamp, 10);
    if (age > REFERRAL_MAX_AGE_MS) {
      // Expired - clean up
      clearStoredReferralCode();
      return null;
    }

    return code;
  } catch {
    return null;
  }
}

/**
 * Clear the stored referral code after it has been used
 */
export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
    localStorage.removeItem(REFERRAL_TIMESTAMP_KEY);
  } catch {
    // Silently fail
  }
}
